import { createAction, createActionGroup, Creator, props } from "@ngrx/store";

import { type ValueEqualityFn } from "@angular/core";

export interface Action<Type extends string = string> {
  type: Type;
}
export type ActionType<A> =
  A extends ActionCreator<infer T, infer C>
    ? ReturnType<C> & {
        type: T;
      }
    : never;
export type TypeId<T> = () => T;
export type InitialState<T> = Partial<T> | TypeId<Partial<T>> | void;
/**
 * A function that takes an `Action` and a `State`, and returns a `State`.
 * See `createReducer`.
 */
export interface ActionReducer<T, V extends Action = Action> {
  (state: T | undefined, action: V): T;
}
export type ActionReducerMap<T, V extends Action = Action> = {
  [p in keyof T]: ActionReducer<T[p], V>;
};
export interface ActionReducerFactory<T, V extends Action = Action> {
  (
    reducerMap: ActionReducerMap<T, V>,
    initialState?: InitialState<T>,
  ): ActionReducer<T, V>;
}
export type MetaReducer<T = any, V extends Action = Action> = (
  reducer: ActionReducer<T, V>,
) => ActionReducer<T, V>;
export interface StoreFeature<T, V extends Action = Action> {
  key: string;
  reducers: ActionReducerMap<T, V> | ActionReducer<T, V>;
  reducerFactory: ActionReducerFactory<T, V>;
  initialState?: InitialState<T>;
  metaReducers?: MetaReducer<T, V>[];
}
export type Selector<T, V> = (state: T) => V;
/**
 * @deprecated Selectors with props are deprecated, for more info see {@link https://github.com/ngrx/platform/issues/2980 Github Issue}
 */
export type SelectorWithProps<State, Props, Result> = (
  state: State,
  props: Props,
) => Result;
export declare const arraysAreNotAllowedMsg =
  "action creator cannot return an array";
type ArraysAreNotAllowed = typeof arraysAreNotAllowedMsg;
export declare const typePropertyIsNotAllowedMsg =
  "action creator cannot return an object with a property named `type`";
type TypePropertyIsNotAllowed = typeof typePropertyIsNotAllowedMsg;
export declare const emptyObjectsAreNotAllowedMsg =
  "action creator cannot return an empty object";
type EmptyObjectsAreNotAllowed = typeof emptyObjectsAreNotAllowedMsg;
export declare const arraysAreNotAllowedInProps =
  "action creator props cannot be an array";
type ArraysAreNotAllowedInProps = typeof arraysAreNotAllowedInProps;
export declare const typePropertyIsNotAllowedInProps =
  "action creator props cannot have a property named `type`";
type TypePropertyIsNotAllowedInProps = typeof typePropertyIsNotAllowedInProps;
export declare const emptyObjectsAreNotAllowedInProps =
  "action creator props cannot be an empty object";
type EmptyObjectsAreNotAllowedInProps = typeof emptyObjectsAreNotAllowedInProps;
export declare const primitivesAreNotAllowedInProps =
  "action creator props cannot be a primitive value";
type PrimitivesAreNotAllowedInProps = typeof primitivesAreNotAllowedInProps;
export type FunctionIsNotAllowed<
  T,
  ErrorMessage extends string,
> = T extends Function ? ErrorMessage : T;
/**
 * A function that returns an object in the shape of the `Action` interface.  Configured using `createAction`.
 */
export type Primitive =
  | string
  | number
  | bigint
  | boolean
  | symbol
  | null
  | undefined;
export type NotAllowedCheck<T extends object> = T extends any[]
  ? ArraysAreNotAllowed
  : T extends {
        type: any;
      }
    ? TypePropertyIsNotAllowed
    : keyof T extends never
      ? EmptyObjectsAreNotAllowed
      : unknown;
export type NotAllowedInPropsCheck<T> = T extends object
  ? T extends any[]
    ? ArraysAreNotAllowedInProps
    : T extends {
          type: any;
        }
      ? TypePropertyIsNotAllowedInProps
      : keyof T extends never
        ? EmptyObjectsAreNotAllowedInProps
        : unknown
  : T extends Primitive
    ? PrimitivesAreNotAllowedInProps
    : never;
/**
 * See `Creator`.
 */
export type ActionCreator<
  T extends string = string,
  C extends Creator = Creator,
> = C & Action<T>;
export interface ActionCreatorProps<T> {
  _as: "props";
  _p: T;
}
export type FunctionWithParametersType<P extends unknown[], R = void> = (
  ...args: P
) => R;
export interface RuntimeChecks {
  /**
   * Verifies if the state is serializable
   */
  strictStateSerializability: boolean;
  /**
   * Verifies if the actions are serializable. Please note, you may not need to set it to `true` unless you are storing/replaying actions using external resources, for example `localStorage`.
   */
  strictActionSerializability: boolean;
  /**
   * Verifies that the state isn't mutated
   */
  strictStateImmutability: boolean;
  /**
   * Verifies that actions aren't mutated
   */
  strictActionImmutability: boolean;
  /**
   * Verifies that actions are dispatched within NgZone
   */
  strictActionWithinNgZone: boolean;
  /**
   * Verifies that action types are not registered more than once
   */
  strictActionTypeUniqueness?: boolean;
}
export interface SelectSignalOptions<T> {
  /**
   *  A comparison function which defines equality for select results.
   */
  equal?: ValueEqualityFn<T>;
}
export type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};
export {};

type Join<
  Str extends string,
  Separator extends string = " ",
> = Str extends `${infer First}${Separator}${infer Rest}`
  ? Join<`${First}${Rest}`, Separator>
  : Str;
type CapitalizeWords<Str extends string> =
  Str extends `${infer First} ${infer Rest}`
    ? `${Capitalize<First>} ${CapitalizeWords<Rest>}`
    : Capitalize<Str>;
type StringLiteralCheck<
  Str extends string,
  Name extends string,
> = string extends Str ? `${Name} must be a string literal type` : unknown;
type UniqueEventNameCheck<EventNames extends string, EventName extends string> =
  ActionName<EventName> extends ActionName<Exclude<EventNames, EventName>>
    ? `${ActionName<EventName>} action is already defined`
    : unknown;
type NotAllowedEventPropsCheck<
  PropsCreator extends ActionCreatorProps<unknown> | Creator,
> =
  PropsCreator extends ActionCreatorProps<infer Props>
    ? Props extends void
      ? unknown
      : NotAllowedCheck<Props & object>
    : PropsCreator extends Creator<any, infer Result>
      ? NotAllowedCheck<Result>
      : unknown;
type EventCreator<
  PropsCreator extends ActionCreatorProps<unknown> | Creator,
  Type extends string,
> =
  PropsCreator extends ActionCreatorProps<infer Props>
    ? void extends Props
      ? ActionCreator<Type, () => Action<Type>>
      : ActionCreator<
          Type,
          (
            props: Props & NotAllowedCheck<Props & object>,
          ) => Props & Action<Type>
        >
    : PropsCreator extends Creator<infer Props, infer Result>
      ? FunctionWithParametersType<
          Props,
          Result & NotAllowedCheck<Result> & Action<Type>
        > &
          Action<Type>
      : never;
type ActionName<EventName extends string> = Uncapitalize<
  Join<CapitalizeWords<EventName>>
>;
type InstanceActionName<EventName extends string, InstanceId extends string> =
  Uncapitalize<`${Join<CapitalizeWords<EventName>>}`>;
interface ActionGroupConfig<
  Source extends string,
  Events extends Record<string, ActionCreatorProps<unknown> | Creator>,
> {
  source: Source & StringLiteralCheck<Source, "source">;
  events: Events & {
    [EventName in keyof Events]: StringLiteralCheck<
      EventName & string,
      "event name"
    > &
      UniqueEventNameCheck<keyof Events & string, EventName & string> &
      NotAllowedEventPropsCheck<Events[EventName]>;
  };
}
interface InstanceActionGroupConfig<
  Source extends string,
  InstanceId extends string,
  Events extends Record<string, ActionCreatorProps<unknown> | Creator>,
> {
  source: Source;
  instanceId: InstanceId,
  events: Events & {
    [EventName in keyof Events]: StringLiteralCheck<
      EventName & string,
      "event name"
    > &
      UniqueEventNameCheck<keyof Events & string, EventName & string> &
      NotAllowedEventPropsCheck<Events[EventName]>;
  };
}
export type ActionGroup<
  Source extends string,
  Events extends Record<string, ActionCreatorProps<unknown> | Creator>,
> = {
  [EventName in keyof Events as ActionName<EventName & string>]: EventCreator<
    Events[EventName],
    `[${Source}] ${EventName & string}`
  >;
};

export type InstanceActionGroup<
  Source extends string,
  InstanceId extends string,
  Events extends Record<string, ActionCreatorProps<unknown> | Creator>,
> = {
  [EventName in keyof Events as InstanceActionName<
    EventName & string, InstanceId & string
  >]: EventCreator<
    Events[EventName],
    `[${Source}-${InstanceId}] ${EventName & string}`
  >;
} ;


export function createInstanceActionGroup<
  Source extends string,
  InstanceId extends string,
  Events extends Record<string, ActionCreatorProps<unknown> | Creator>,
>(
  config: InstanceActionGroupConfig<Source, InstanceId, Events>,
): InstanceActionGroup<Source, InstanceId, Events> {
  const { source, instanceId, events } = config;
  const iag = Object.keys(events).reduce(
    (actionGroup, eventName) => ({
      ...actionGroup,
      [toInstanceActionName(eventName, instanceId)]: createAction(
        toInstanceActionType(source, instanceId, eventName),
        (events as any)[eventName],
      ),
    }),
    {} as InstanceActionGroup<Source, InstanceId, Events>,
  );
  return iag;
}

export function capitalize<T extends string>(text: T): Capitalize<T> {
  return (text.charAt(0).toUpperCase() + text.substring(1)) as Capitalize<T>;
}

export function uncapitalize<T extends string>(text: T): Uncapitalize<T> {
  return (text.charAt(0).toLowerCase() + text.substring(1)) as Uncapitalize<T>;
}

function toInstanceActionName<EventName extends string, InstanceId extends string>(
  eventName: EventName,
  instanceId: InstanceId,
): InstanceActionName<EventName, InstanceId> {
  return `${eventName}`
    .trim()
    .split(" ")
    .map((word, i) => (i === 0 ? uncapitalize(word) : capitalize(word)))
    .join("") as InstanceActionName<EventName, InstanceId>;
}

function toInstanceActionType<Source extends string, InstanceId extends string, EventName extends string>(
  source: Source,
  instanceId: InstanceId,
  eventName: EventName,
): `[${Source}-${InstanceId}] ${EventName}` {
  return `[${source}-${instanceId}] ${eventName}`;
}

